import {useEffect, useRef} from 'react';
import type {DirectMessageRoomOutcome} from '../../services/matrixService';
import {resolveOrCreateDirectRoom} from './directRoomLookup';

export interface OpenDirectChatDeps {
    resolveMatrixId: (appUserId: string) => Promise<string | undefined>;
    lookup: (matrixId: string) => DirectMessageRoomOutcome;
    waitForOutcome: (matrixId: string) => Promise<DirectMessageRoomOutcome>;
    findLocalDirectRoom: (appUserId: string) => string | undefined;
    createDirectRoom: (matrixId: string) => Promise<string>;
    selectRoom: (roomId: string) => Promise<void>;
}

export class UnresolvedChatUserError extends Error {
    constructor(appUserId: string) {
        super(`Could not resolve a chat identity for user ${appUserId}`);
        this.name = 'UnresolvedChatUserError';
    }
}

export async function performOpenDirectChat(appUserId: string, deps: OpenDirectChatDeps): Promise<string> {
    const matrixId = await deps.resolveMatrixId(appUserId);
    if (!matrixId) {
        throw new UnresolvedChatUserError(appUserId);
    }

    const roomId = await resolveOrCreateDirectRoom({
        peerId: matrixId,
        lookup: () => deps.lookup(matrixId),
        waitForOutcome: () => deps.waitForOutcome(matrixId),
        findLocalFallback: () => deps.findLocalDirectRoom(appUserId),
        createRoom: () => deps.createDirectRoom(matrixId),
    });

    await deps.selectRoom(roomId);
    return roomId;
}

export interface UseOpenDirectChatFromStateArgs extends OpenDirectChatDeps {
    pendingUserId: string | undefined;
    isReady: boolean;
    consumeLocationState: () => void;
    onError: (error: unknown) => void;
}

export function useOpenDirectChatFromState(args: UseOpenDirectChatFromStateArgs): void {
    const argsRef = useRef(args);
    argsRef.current = args;
    const inFlightRef = useRef<Set<string>>(new Set());

    const {pendingUserId, isReady} = args;

    useEffect(() => {
        if (!pendingUserId || !isReady) return;

        const current = argsRef.current;
        current.consumeLocationState();

        if (inFlightRef.current.has(pendingUserId)) return;
        inFlightRef.current.add(pendingUserId);

        performOpenDirectChat(pendingUserId, current)
            .catch(current.onError)
            .finally(() => inFlightRef.current.delete(pendingUserId));
    }, [pendingUserId, isReady]);
}
